import { get, keyBy } from 'lodash';
import React from 'react';
import { useCombobox, UseComboboxProps, useMultipleSelection, UseMultipleSelectionProps } from 'downshift';
import { ChevronDown } from 'styled-icons/fa-solid/ChevronDown';

import { getStyledSystemProps, getSearchStringRegExp } from 'src/helpers';
import { BaseBox } from '../Box';
import { Label, LabelProps } from '../Label';
import { BaseStyledInput, BaseStyledInputProps } from '../StyledInput';
import { DefaultMultiValuesWrapper } from '../Select';
import { useComboboxPositioning } from './useComboboxPositioning';
import { StyledContainer, StyledContainerProps, StyledComboboxControl } from './Combobox.styled';
import { Menu } from './Menu';
import { InnerSelectedItem } from './InnerSelectedItem';
import { OuterSelectedItem } from './OuterSelectedItem';

export type MultiComboboxProps = StyledContainerProps &
  UseComboboxProps<any> &
  UseMultipleSelectionProps<any> &
  LabelProps &
  BaseStyledInputProps & {
    children?: React.ReactNode;
    popperProps?: any;
    multiValuesWrapper?: React.ReactNode;
    classicAppearance?: boolean;
    shape?: { valueKey: string; labelKey: string };
    error?: boolean | string;
    inputProps?: any;
    onChange?: (selectedItems: any[]) => void;
    onSelect?: (item: any, items: any[]) => void;
    onRemove?: (item: any, items: any[]) => void;
    onCreate?: (inputValue: string) => void;
    onInputChange?: (inputValue: string) => void;
    isCreatable?: boolean;
    formatCreateLabel?(inputValue: string): string;
  };

export const MultiCombobox = React.forwardRef(
  (
    {
      children,
      name,
      value,
      defaultValue,
      items,
      shape: { valueKey, labelKey },
      onChange,
      onBlur,
      onSelect,
      onRemove,
      onCreate,
      onInputChange,
      label,
      placeholder,
      disabled,
      isCreatable,
      formatCreateLabel,
      multiValuesWrapper: MultiValuesWrapper,
      classicAppearance,
      error,
      variant,
      size,
      popperProps = {},
      ...props
    }: MultiComboboxProps,
    outerRef: (node: HTMLElement | null) => void
  ) => {
    const [styledProps, restProps] = getStyledSystemProps(props);
    const shadowInputTextRef = React.useRef(null);
    const [inputValue, setInputValue] = React.useState('');
    const [inputWidth, setInputWidth] = React.useState(0);
    const getItemValue = React.useCallback(item => get(item, valueKey, item), [valueKey]);
    const getItemLabel = React.useCallback(item => get(item, labelKey, item), [labelKey]);
    const { referenceElementRef, popperElementRef, styles, attributes, update } = useComboboxPositioning(popperProps);

    const {
      getDropdownProps,
      getSelectedItemProps,
      addSelectedItem,
      removeSelectedItem,
      selectedItems
    } = useMultipleSelection({
      itemToString: getItemLabel,
      selectedItems: value,
      defaultSelectedItems: defaultValue,
      onSelectedItemsChange: ({ selectedItems }) => {
        if (onChange) {
          onChange(selectedItems);
        }
        update?.();
      }
    });

    const handleSelectItem = React.useCallback(
      selectedItem => {
        addSelectedItem(selectedItem);
        if (onSelect) {
          onSelect(selectedItem, selectedItems);
        }
      },
      [addSelectedItem, onSelect]
    );

    const handleRemoveItem = React.useCallback(
      selectedItem => {
        removeSelectedItem(selectedItem);
        if (onRemove) {
          onRemove(selectedItem, selectedItems);
        }
      },
      [onRemove, removeSelectedItem]
    );

    const handleCreateItem = React.useCallback(
      inputValue => {
        if (onCreate) {
          onCreate(inputValue);
        } else {
          addSelectedItem(inputValue);
        }
        setInputValue('');
      },
      [addSelectedItem, onCreate]
    );

    const searchStringRegExp = React.useMemo(() => getSearchStringRegExp(inputValue), [inputValue]);

    const selectedItemsMap = keyBy(selectedItems, getItemValue);

    const filterItemsBySearchQuery = React.useCallback(
      (acc, item) => {
        const itemLabel = getItemLabel(item)
          .trim()
          .toLowerCase();
        if (classicAppearance || !selectedItemsMap[getItemValue(item)]) {
          const index = itemLabel.search(searchStringRegExp);
          if (index !== -1) {
            acc.push({ item, index });
          }
        }
        return acc;
      },
      [classicAppearance, getItemLabel, getItemValue, searchStringRegExp, selectedItemsMap]
    );

    const menuItems = React.useMemo(
      () =>
        items
          .reduce((acc, item) => {
            if (item.items) {
              const filteredItems = item.items
                .reduce(filterItemsBySearchQuery, [])
                .sort((item1, item2) => item1.index - item2.index)
                .map(({ item }) => item);

              if (filteredItems.length) {
                acc.push({
                  ...item,
                  items: filteredItems
                });
              }
              return acc;
            }
            return filterItemsBySearchQuery(acc, item);
          }, [])
          .sort((item1, item2) => item1.index - item2.index)
          .map(item => (item.item ? item.item : item)),
      [filterItemsBySearchQuery, items]
    );

    const flatMenuItems = menuItems.flatMap(item => item.items || item);

    const {
      getComboboxProps,
      getLabelProps,
      getInputProps,
      getToggleButtonProps,
      getMenuProps,
      getItemProps,
      isOpen,
      highlightedIndex,
      selectItem
    } = useCombobox({
      inputValue,
      items: flatMenuItems,
      stateReducer: (state, actions) => {
        if (actions.type === useCombobox.stateChangeTypes.ItemClick) {
          return {
            ...actions.changes,
            isOpen: true
          };
        }
        return actions.changes;
      },
      onStateChange: ({ inputValue, type, selectedItem }) => {
        switch (type) {
          case useCombobox.stateChangeTypes.InputChange:
            setInputValue(inputValue || '');
            setInputWidth(shadowInputTextRef.current.clientWidth);
            if (onInputChange) {
              onInputChange(inputValue);
            }
            break;
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
            setInputValue('');
            if (selectedItem) {
              if (selectedItemsMap[getItemValue(selectedItem)]) {
                handleRemoveItem(selectedItem);
              } else {
                handleSelectItem(selectedItem);
              }
              selectItem(null);
            }
            break;
          case useCombobox.stateChangeTypes.InputBlur:
            setInputValue('');
            if (onBlur) {
              onBlur();
            }
            break;
          default:
            break;
        }
      },
      ...restProps
    });

    const computedLabel =
      label && classicAppearance && selectedItems.length ? `${label}: ${selectedItems.length}` : label;

    const isValidNewItem = !selectedItems.some(item => getItemLabel(item) === inputValue);

    return (
      <StyledContainer ref={outerRef} {...styledProps}>
        <Label as="label" label={computedLabel} disabled={disabled} {...getLabelProps()} />
        <StyledComboboxControl
          {...{ variant, size, error, classicAppearance }}
          {...getComboboxProps()}
          {...getToggleButtonProps({ ref: referenceElementRef, disabled })}
        >
          <BaseBox as={ChevronDown} className="chevron" size="10px" color="textSecondary" />
          {classicAppearance &&
            selectedItems.map((selectedItem, index) => (
              <InnerSelectedItem
                key={`selected-item-${index}`}
                {...getSelectedItemProps({ selectedItem, index })}
                onRemove={() => handleRemoveItem(selectedItem)}
              >
                {getItemLabel(selectedItem)}
              </InnerSelectedItem>
            ))}
          <BaseStyledInput
            {...getInputProps(
              getDropdownProps({
                name,
                placeholder: classicAppearance && selectedItems.length ? undefined : placeholder,
                disabled
              })
            )}
            className="search-input"
            variant="ghost"
            size={size}
            style={{
              width: classicAppearance && selectedItems.length ? (inputValue.length ? inputWidth + 20 : 0) : 'auto'
            }}
          />
          <div ref={shadowInputTextRef} css={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'pre' }}>
            {inputValue}
          </div>
          <div ref={popperElementRef} style={styles.popper} {...attributes.popper} css={{ zIndex: 10, width: '100%' }}>
            {isOpen && (
              <Menu
                {...{
                  highlightedIndex,
                  getMenuProps,
                  getItemProps,
                  getItemValue,
                  getItemLabel,
                  items: menuItems,
                  selectedItemsMap,
                  classicAppearance,
                  inputValue,
                  isCreatable,
                  isValidNewItem,
                  formatCreateLabel,
                  onCreate: handleCreateItem,
                  size
                }}
              />
            )}
          </div>
        </StyledComboboxControl>
        {children}
        {!classicAppearance && (
          <MultiValuesWrapper>
            {selectedItems.map((selectedItem, index) => (
              <OuterSelectedItem
                key={`selected-item-${index}`}
                {...getSelectedItemProps({ selectedItem, index, size })}
                onRemove={() => handleRemoveItem(selectedItem)}
              >
                {getItemLabel(selectedItem)}
              </OuterSelectedItem>
            ))}
          </MultiValuesWrapper>
        )}
      </StyledContainer>
    );
  }
);

MultiCombobox.defaultProps = {
  value: [],
  placeholder: 'Select options',
  multiValuesWrapper: DefaultMultiValuesWrapper,
  shape: { valueKey: '', labelKey: '' },
  formatCreateLabel: inputValue => `Add ${inputValue}?`,
  inputProps: {},
  isCreatable: false,
  variant: 'primary',
  size: 'md'
};

MultiCombobox.displayName = 'MultiCombobox';
